import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "children", "fluid", "variant", "withDarkBackground", "size", "onClick", "dropdown", "PopperProps", "PaperProps", "disabled"],
  _excluded2 = ["sizeDefault", "textPrimaryDark", "dropdownButtonActive", "dropdownButtonContainer", "textDropdownButtonActive", "svgColor", "svgColorTextVariant", "dropdownSecondaryButtonActive"];
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { default as withStyles } from '../styles';
import { default as MuiButton } from '@mui/material/Button';
import { borderRadiusSmall, borderWidthThin, fontFamilyCondensed, fontWeightBold, fontSizeLarge, lineHeightDefault, sizingXLarge, sizingXxLarge, spacing20, spacing40, boxShadowFocus } from '../styles/tokens';
import ChevronDownIcon from '@ellucian/ds-icons/lib/ChevronDown';
import ChevronUpIcon from '@ellucian/ds-icons/lib/ChevronUp';
import DropdownPopper from './internal/DropdownPopper';

/**
 * TODO: Need to implement the css for outlined buttons now that they are available.
 */
export var styles = function styles(theme) {
  return {
    // Shared Button styles
    root: {
      // fontFamily is being updated from theme object for fontFamilies: { button: ... }
      boxShadow: 'none',
      color: theme.palette.text.white,
      borderRadius: borderRadiusSmall,
      minHeight: sizingXLarge,
      // replace with token
      outline: 'none',
      padding: spacing20 + " " + spacing40,
      '&$disabled': {
        color: theme.palette.text.white
      },
      '& svg': {
        flex: '1 0 auto'
      }
    },
    // Sizes
    sizeDefault: {},
    sizeLarge: {
      fontSize: theme.typography.h3.fontSize,
      minHeight: sizingXxLarge,
      padding: spacing20 + " " + spacing40
    },
    // Shared Disabled Button styles
    disabled: {
      backgroundColor: theme.palette.disabled.background,
      color: theme.palette.grey[100]
    },
    // Primary Button
    contained: {
      backgroundColor: theme.palette.ctaColor.base,
      fill: theme.palette.text.white,
      '&:hover': {
        backgroundColor: theme.palette.ctaColor.hover,
        borderColor: theme.palette.ctaColor.hover,
        boxShadow: 'none'
      },
      '&:active': {
        boxShadow: 'none',
        backgroundColor: theme.palette.ctaColor.active,
        borderColor: theme.palette.ctaColor.active
      },
      '&$disabled': {
        boxShadow: 'none',
        border: 'none',
        backgroundColor: theme.palette.disabled.background,
        color: theme.palette.text.white
      },
      '&:focus': {
        outline: 'none',
        boxShadow: boxShadowFocus
      },
      '&:focus:hover': {
        backgroundColor: theme.palette.ctaColor.active,
        borderColor: theme.palette.ctaColor.active,
        boxShadow: boxShadowFocus
      }
    },
    text: {},
    // text Button
    textPrimary: {
      backgroundColor: 'transparent',
      color: theme.palette.ctaColor.base,
      // cta blue 600
      fill: theme.palette.ctaColor.base,
      borderRadius: borderRadiusSmall,
      minWidth: 'inherit',
      minHeight: 'inherit',
      height: 'auto',
      '&$disabled': {
        boxShadow: 'none',
        border: 'none',
        color: theme.palette.action.disabled,
        backgroundColor: 'transparent',
        fill: theme.palette.action.disabled
      },
      '&:focus:active': {
        color: theme.palette.action.active,
        // cta blue 800
        boxShadow: 'none',
        fill: theme.palette.action.active
      },
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.action.hover,
        // cta blue 700
        boxShadow: 'none',
        fill: theme.palette.action.hover
      },
      '&:focus': {
        outline: 'none',
        boxShadow: boxShadowFocus
      },
      '&:focus:hover': {
        boxShadow: 'none'
      }
    },
    textStandalone: {
      fontFamily: fontFamilyCondensed,
      fontSize: fontSizeLarge,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em',
      letterSpacing: '0.0625em',
      padding: spacing20 + " " + spacing40,
      '&:hover': {
        backgroundColor: theme.palette.ctaColor.hover,
        color: theme.palette.text.white,
        fill: theme.palette.text.white
      },
      '&:active': {
        backgroundColor: theme.palette.ctaColor.active
      },
      '&:focus:active': {
        color: theme.palette.text.white,
        fill: theme.palette.text.white
      }
    },
    textPrimaryDark: {
      color: theme.palette.grey[100],
      '&:hover': {
        color: theme.palette.grey[200]
      },
      '&:focus:active': {
        color: theme.palette.grey[300]
      }
    },
    // Secondary Button
    containedSecondary: {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.ctaColor.base,
      fill: theme.palette.ctaColor.base,
      border: borderWidthThin + " solid",
      '&:hover': {
        backgroundColor: theme.palette.ctaColor.hover,
        borderColor: theme.palette.ctaColor.hover,
        color: theme.palette.grey[100],
        fill: theme.palette.grey[100]
      },
      '&:active': {
        boxShadow: 'none',
        backgroundColor: theme.palette.ctaColor.active,
        borderColor: theme.palette.ctaColor.active,
        color: theme.palette.grey[100],
        fill: theme.palette.text.white
      },
      '&$disabled': {
        boxShadow: 'none',
        border: 'none',
        backgroundColor: theme.palette.disabled.background,
        color: theme.palette.text.white,
        fill: theme.palette.text.white
      },
      '&:focus': {
        outline: 'none',
        boxShadow: boxShadowFocus
      },
      '&:focus:hover': {
        backgroundColor: theme.palette.ctaColor.active,
        borderColor: theme.palette.ctaColor.active,
        boxShadow: boxShadowFocus,
        color: theme.palette.text.white,
        fill: theme.palette.text.white
      }
    },
    dropdownButtonContainer: {
      display: 'inline-block',
      position: 'relative'
    },
    dropdownButtonActive: {
      backgroundColor: theme.palette.ctaColor.active,
      borderColor: theme.palette.ctaColor.active,
      color: theme.palette.text.white,
      fill: theme.palette.text.white
    },
    dropdownSecondaryButtonActive: {
      backgroundColor: theme.palette.ctaColor.active,
      borderColor: theme.palette.ctaColor.active,
      color: theme.palette.text.white,
      fill: theme.palette.text.white
    },
    textDropdownButtonActive: {
      color: theme.palette.ctaColor.active,
      fill: theme.palette.action.active
    },
    // override MUI's attempts to set the first icon size
    iconSize: {
      '&>*:nth-of-type(1)': {
        fontSize: fontSizeLarge
      }
    }

    // Outlined => Inverted Buttons
    // outlined: {
    //     backgroundColor: "transparent",
    //     color: "#FFFFFF",
    //     border: "1px solid #FFFFFF",
    //     "&:hover": {
    //         backgroundColor: "#FFFFFF",
    //         color: "transparent" // TODO: verify with UXD about color fo rtext on hover, pressed, focused
    //     },
    //     "&:active": {
    //         boxShadow: "none",
    //         backgroundColor: "#0A4982",
    //         borderColor: "#0A4982",
    //         color: "#FFFFFF"
    //     },
    //     "&$disabled": {
    //         color: "#FFFFFF", // FIXME: not showing up properly, being overridden
    //         boxShadow: "none",
    //         backgroundColor: "#B2B3B7",
    //         border: "none"
    //     },
    //     "&:focus": {
    //         boxShadow: "0 0 0 2px #FFFFFF"
    //     },
    //     "&:focus:hover": {
    //         backgroundColor: "#FFFFFF",
    //         borderColor: "#FFFFFF",
    //         boxShadow: "0 0 0 2px #FFFFFF",
    //         color: "transparent"
    //     }
    // },
  };
};

/**
 * Use `Button` to show a call to action.
 * @done true
 * @updated v7.18.0
 * @versionAdded v0.0.5
 * @examples
 *  SimpleExample
 *  SizesExample
 *  FluidExample
 *  TextWithIconExample
 *  ButtonGroupExample
 *  TextButtonExample
 *  TextOverflowExample
 *  ButtonDropdownExample
 */
var Button = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _cn;
  var classes = props.classes,
    children = props.children,
    fluid = props.fluid,
    variant = props.variant,
    withDarkBackground = props.withDarkBackground,
    size = props.size,
    onClick = props.onClick,
    dropdown = props.dropdown,
    PopperProps = props.PopperProps,
    PaperProps = props.PaperProps,
    disabled = props.disabled,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var _React$useState = React.useState(false),
    isMenuOpen = _React$useState[0],
    setIsMenuOpen = _React$useState[1];
  var PopperPropsProps = Object.assign({}, PopperProps);
  var buttonRef = ref || React.useRef(); //eslint-disable-line react-hooks/rules-of-hooks
  var ariaProps = {};

  // Extra our custom classes so they aren't passed to MUI
  var sizeDefault = classes.sizeDefault,
    textPrimaryDark = classes.textPrimaryDark,
    dropdownButtonActive = classes.dropdownButtonActive,
    dropdownButtonContainer = classes.dropdownButtonContainer,
    textDropdownButtonActive = classes.textDropdownButtonActive,
    svgColor = classes.svgColor,
    svgColorTextVariant = classes.svgColorTextVariant,
    dropdownSecondaryButtonActive = classes.dropdownSecondaryButtonActive,
    restClasses = _objectWithoutPropertiesLoose(classes, _excluded2);
  var textClass = classes.textPrimary + " " + classes.text + "\n        " + (withDarkBackground ? textPrimaryDark : '') + "\n        " + classes.textStandalone + "\n    ";
  var propOverrides = {
    disableRipple: true,
    // disable the click ripple by default
    disableFocusRipple: true,
    // disable the default MUI focus ripple effect
    size: size
  };
  if (dropdown) {
    ariaProps['aria-haspopup'] = 'listbox';
  }
  if (variant === 'text') {
    // Need to override these props to prevent styling issues
    propOverrides.color = 'primary';
    propOverrides.fullWidth = false;
  }
  switch (size) {
    case 'small' || 'medium':
      console.warn("Size=\"" + size + "\" has been removed. Size is set to \"default\".");
      propOverrides.size = 'medium'; // needs to be set to MUI size, "medium"
      break;
    case 'default':
      propOverrides.size = 'medium'; // needs to be set to MUI size, "medium"
      break;
    default:
      break;
  }
  var classOverrides = _extends({}, restClasses, {
    // pass down the rest of MUI supported classes
    root: cn(classes.root, (_cn = {}, _cn[sizeDefault] = propOverrides.size === 'medium', _cn[dropdownButtonActive] = isMenuOpen && variant === 'contained', _cn[textDropdownButtonActive] = isMenuOpen && variant === 'text', _cn[dropdownSecondaryButtonActive] = isMenuOpen && props.color === 'secondary', _cn)),
    contained: classes.contained,
    text: textClass,
    containedSecondary: classes.containedSecondary,
    sizeLarge: classes.sizeLarge,
    disabled: classes.disabled,
    endIcon: classes.iconSize,
    startIcon: classes.iconSize
  });

  // Disable button if all items are disabled in the list.
  var shouldDropdownButtonDisable = function shouldDropdownButtonDisable() {
    return props.dropdown && React.Children.toArray(props.dropdown).every(function (child) {
      if (!child.props.disabled) {
        return false;
      } else {
        return true;
      }
    });
  };
  var onClickHandler = function onClickHandler(e) {
    if (!dropdown && onClick) {
      onClick(e);
    } else if (dropdown) {
      e.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    }
  };
  var getButtonComponent = function getButtonComponent() {
    var _cn2, _cn3;
    return /*#__PURE__*/React.createElement(MuiButton, _extends({
      variant: variant,
      disabled: disabled || dropdown && shouldDropdownButtonDisable(),
      classes: classOverrides,
      fullWidth: fluid,
      size: propOverrides.size,
      ref: buttonRef,
      onClick: onClickHandler,
      endIcon: dropdown && (isMenuOpen ? /*#__PURE__*/React.createElement(ChevronUpIcon, {
        className: cn((_cn2 = {}, _cn2[svgColor] = variant === 'contained', _cn2[svgColorTextVariant] = variant === 'text', _cn2))
      }) : /*#__PURE__*/React.createElement(ChevronDownIcon, {
        className: cn((_cn3 = {}, _cn3[svgColor] = variant === 'contained', _cn3[svgColorTextVariant] = variant === 'text', _cn3))
      }))
    }, rest, propOverrides, ariaProps), children);
  };
  var offset = [0, 4]; // Bring popper closer to icon
  if (PopperPropsProps.modifiers) {
    PopperPropsProps.modifiers = [].concat(PopperPropsProps.modifiers, [{
      name: 'offset',
      options: {
        offset: offset
      }
    }]);
  } else {
    PopperPropsProps.modifiers = [{
      name: 'offset',
      options: {
        offset: offset
      }
    }];
  }
  return !dropdown ? getButtonComponent() : /*#__PURE__*/React.createElement("div", {
    className: cn(dropdownButtonContainer)
  }, getButtonComponent(), dropdown && /*#__PURE__*/React.createElement(DropdownPopper, {
    buttonRef: buttonRef,
    isMenuOpen: isMenuOpen,
    onClickHandler: onClickHandler,
    PaperProps: _extends({}, PaperProps),
    PopperProps: _extends({}, PopperPropsProps),
    dropdown: dropdown
  }));
});
Button.propTypes = {
  /**
   * Class name to assign the component.
   */
  className: PropTypes.string,
  /**
   * Classes used to extend original style object.
   */
  classes: PropTypes.object,
  /**
   * The content of the button.
   */
  children: PropTypes.node.isRequired,
  /**
   * @ignore
   * The component used for the root node. Either a string to use a HTML element or a component.
   */
  component: PropTypes.element,
  /**
   * The color of the component.
   */
  color: PropTypes.oneOf(['primary', 'secondary']),
  // can also support primary, secondary, inherit, success, info, warning, and any new custom colors added to theme
  /**
   * If `true`, the button will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * @ignore
   * If `true`, no elevation is used.
   */
  disableElevation: PropTypes.bool,
  /**
   * @ignore
   * If `true`, the kyeboard focus ripple is disabled.
   */
  disableFocusRipple: PropTypes.bool,
  /**
   * @ignore
   * If `true`, the ripple effect is disabled.
   */
  disableRipple: PropTypes.bool,
  /**
   * The elements to populate dropdown button list items. These must be an array of `DropdownButtonItem` elements.
   *
   * **Only valid for dropdown button.**
   */
  dropdown: PropTypes.array,
  /**
   * Element placed after the children
   */
  endIcon: PropTypes.node,
  /**
   * If `true`, the button will take up the full width of its container.
   */
  fluid: PropTypes.bool,
  /**
   * The URL to link to when the button is clicked.
   * If defined, an `a` element will be used as the root node.
   */
  href: PropTypes.string,
  /**
   * The size of the button.
   */
  size: PropTypes.oneOf(['default', 'large']),
  // can also support small, medium and large
  /**
   * Element placed before the children
   */
  startIcon: PropTypes.node,
  /**
   * 	The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * The HTML button type.
   */
  type: PropTypes.string,
  /**
   * The type of button.
   */
  variant: PropTypes.oneOf(['contained', 'text']),
  /**
   * With `variant="text"`, use this if you want to render a button on Snackbar or any dark background.
   */
  withDarkBackground: PropTypes.bool,
  // We should use "flat" for our "text" buttons, and "outlined" for our "inverted", and "fab" for our floating action button.
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * @ignore
   */
  PaperProps: PropTypes.object,
  /**
   * @ignore
   */
  PopperProps: PropTypes.object
};
Button.defaultProps = {
  variant: 'contained',
  color: 'primary',
  size: 'default',
  fluid: false,
  withDarkBackground: false
};

// necessary to add an explicit display name, since docgen can't seem to
// extract one from a forwardRef'd component
Button.displayName = 'Button';

/**
 * We order Button styles at index: 0 to avoid improper ordering
 * of Dropdown styles.
 */
export default withStyles(styles, {
  name: 'Button',
  index: 0
})(Button);